/** Stable depth-first ordering. Missing parents become roots (for example after deletion). */
export function orderCommentThreads<T extends { id: number; parentId: number | null }>(
	comments: T[],
): { comment: T; depth: number }[] {
	const ids = new Set(comments.map(comment => comment.id))
	const children = new Map<number | null, T[]>()
	for (const comment of comments) {
		const parentId = comment.parentId !== null && ids.has(comment.parentId) ? comment.parentId : null
		const siblings = children.get(parentId) ?? []
		siblings.push(comment)
		children.set(parentId, siblings)
	}
	const result: { comment: T; depth: number }[] = []
	const seen = new Set<number>()
	const visit = (roots: T[]) => {
		const stack = roots.map(comment => ({ comment, depth: 0 })).reverse()
		while (stack.length) {
			const item = stack.pop()!
			if (seen.has(item.comment.id)) continue
			seen.add(item.comment.id)
			result.push(item)
			for (const comment of [...(children.get(item.comment.id) ?? [])].reverse()) {
				stack.push({ comment, depth: item.depth + 1 })
			}
		}
	}
	visit(children.get(null) ?? [])
	// Defensive fallback for corrupt cycles: never drop comments or recurse forever.
	visit(comments.filter(comment => !seen.has(comment.id)))
	return result
}
